import { IdentityProvider, SwanConfiguration } from '@99designs/design-services-common';
import { PageLayout } from '@99designs/design-services-layouts';
import { OpenPanelLoader, PageProvider, PageTracking } from '@99designs/tracking';
import '@99designs/feature-design-services-landing';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import TrackingJS from '@vp/react-tracking';
import React, { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import Bugsnag from '@bugsnag/js';
import { getAllExperiments, initialize, waitTillAvailable } from '@vp/ab-reader';
import { ScreenClassProvider } from '@vp/swan';
import { NextComponentType, NextPageContext } from 'next';
import type { AppProps } from 'next/app';
import { Breadcrumbs } from '@99designs/design-services-common';
import GenericLayout from '../layouts/GenericLayout';
import {
    ClientOverride,
    authDevMode,
    buildAuthConfiguration,
} from '../lib/OAuth/buildAuthConfiguration';
import i18n from '../lib/i18n';
import initializeDatadogRum from '../lib/initializeDatadogRum';
import '@vp/ep-designer-attribution/designer-attribution.css';
import { PricingContextProvider } from '@vp/react-pricing-context';
import { Helmet } from 'react-helmet-async';
import { VistaExperiment, VistaExperimentsProvider } from '@99designs/experiments';
import { getCountryCode } from '@99designs/i18n';

Bugsnag.start({
    apiKey: process.env.NEXT_PUBLIC_DESIGN_SERVICES_BUGSNAG_API_KEY ?? '',
    enabledReleaseStages: ['production', 'sandbox'],
    releaseStage: process.env.NEXT_PUBLIC_DESIGN_SERVICES_BUGSNAG_RELEASE_STAGE,
    plugins: [new BugsnagPluginReact(React)],
    onError: (event) => {
        event.addMetadata('request', {
            url: event.request.url,
            headers: event.request.headers,
        });

        event.addMetadata('debug info', {
            errorLocation: typeof window === 'undefined' ? 'server' : 'client',
        });
    },
});
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary();

export interface DesignServicesAppProps {
    locale: string;
    pageConfig: { pageTracking: PageTracking; breadcrumbs?: Breadcrumbs };
    experiments: VistaExperiment[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NextPageWithLayout = NextComponentType<NextPageContext, any, DesignServicesAppProps> & {
    layout?: React.ComponentType<{ children: React.ReactNode; locale: string }>;
};

type ModifiedAppProps = AppProps<DesignServicesAppProps> & {
    Component: NextPageWithLayout;
};

function DesignServicesApp({ Component, pageProps }: ModifiedAppProps) {
    const {
        locale,
        pageConfig: { pageTracking, breadcrumbs = [] },
        experiments,
    } = pageProps;
    const env = process.env.NEXT_PUBLIC_DESIGN_SERVICES_TRACKING_ENV ?? 'dev';
    const Sublayout = Component.layout || GenericLayout;
    const [vistaExperiments, setVistaExperiments] = useState<VistaExperiment[]>(experiments);
    const [authConfig, setAuthConfig] = useState<ClientOverride>({
        developmentMode: authDevMode(),
    });

    i18n.changeLanguage(locale);
    useEffect(() => {
        initializeDatadogRum();
    });

    useEffect(() => {
        setAuthConfig(buildAuthConfiguration());
    }, []);

    useEffect(() => {
        //call this as soon as possible in your outermost component
        //this is idempotent so it doesn't matter if called several times by the same or different components
        initialize();

        (async () => {
            const available = await waitTillAvailable(1000);
            if (available) {
                setVistaExperiments((serverExperiments) => {
                    const newExperiments = serverExperiments ? [...serverExperiments] : [];
                    const fetchedExperiments = getAllExperiments();
                    fetchedExperiments.forEach((fetchedExperiment) => {
                        if (
                            !newExperiments.some(
                                (existingExperiment) =>
                                    existingExperiment.experimentKey ===
                                    fetchedExperiment.experimentKey
                            )
                        ) {
                            newExperiments.push(fetchedExperiment as VistaExperiment);
                        }
                    });
                    return newExperiments;
                });
            }
        })();
    }, []);

    return (
        <ErrorBoundary>
            <ScreenClassProvider>
                <SwanConfiguration>
                    <Helmet>
                        <script
                            src="https://pricing-context-module.products.vpsvc.com/static/v3/pricing-context-module.min.js"
                            async
                        />
                    </Helmet>
                    <IdentityProvider locale={locale} {...authConfig}>
                        <IntlProvider locale={locale}>
                            <VistaExperimentsProvider value={vistaExperiments}>
                                <TrackingJS
                                    tenant="vistaprint"
                                    culture={locale}
                                    environment={env}
                                />
                                <OpenPanelLoader
                                    enabled={process.env.NEXT_PUBLIC_OPENPANEL_ENABLED}
                                    clientId={process.env.NEXT_PUBLIC_OPENPANEL_CLIENT_ID}
                                    apiUrl={process.env.NEXT_PUBLIC_OPENPANEL_API_URL}
                                    scriptUrl={process.env.NEXT_PUBLIC_OPENPANEL_SCRIPT_URL}
                                />

                                <PageProvider
                                    locale={locale}
                                    pageData={{
                                        pageName: pageTracking.pageName,
                                        pageStage: pageTracking.pageStage,
                                        pagePath: pageTracking.pagePath,
                                        pageSection: pageTracking.pageSection,
                                    }}
                                >
                                    <PricingContextProvider
                                        initializationConfig={{
                                            merchantId: 'vistaprint',
                                            market: getCountryCode(locale),
                                            isDevelopmentMode:
                                                process.env.NODE_ENV === 'development',
                                        }}
                                    >
                                        <PageLayout
                                            pageTracking={pageTracking}
                                            locale={locale}
                                            breadcrumbs={<Breadcrumbs crumbs={breadcrumbs} />}
                                        >
                                            <Sublayout locale={locale}>
                                                <Component {...pageProps} />
                                            </Sublayout>
                                        </PageLayout>
                                    </PricingContextProvider>
                                </PageProvider>
                            </VistaExperimentsProvider>
                        </IntlProvider>
                    </IdentityProvider>
                </SwanConfiguration>
            </ScreenClassProvider>
        </ErrorBoundary>
    );
}

export default DesignServicesApp;
